import { createSelector, createStructuredSelector } from 'reselect';
import { at, constant, flow, get, isEmpty, sum, invokeArgs } from 'lodash/fp';
import { isMobile, isTablet } from 'react-device-detect';
import { getFormValues } from 'redux-form';

import { stringFormatter } from 'helpers/i18n';
import getCanonicalUrl from 'helpers/getCanonicalUrl';
import {
  selectManufacturerOptions,
  selectCarMakeValues,
} from 'store/ducks/manufacturers';
import createAdItemsGetter from './createAdItemsGetter';
import parseAdsSearch from '../../parseAdsSearch';
import formToFilters from '../../mappers/formToFilters';
import formFromQuery from '../../mappers/formFromQuery';
import { PUBLISHED_NAME } from '../../constants';
import { SEARCH_ADS_FORM, EXTRA_FILTERS_QUERY_KEY } from '../constants';
import {
  SEO_PAGE_MAKE,
  SEO_PAGE_MODEL,
  SEO_PAGE_SUBTYPE,
  SEO_PAGE_CITY,
  SEO_PAGE_CITY_MAKE,
  SEO_PAGE_CITY_MODEL,
} from '../../../../constants/seoPageType';

const DEFAULT_SEO_KEY = 'defaultSeoKey';

const formatters = {
  [SEO_PAGE_MAKE]: {
    htmlTitle: stringFormatter(__('search.makePage.htmlTitle')),
    metaDescription: stringFormatter(__('search.makePage.metaDescription')),
  },
  [SEO_PAGE_MODEL]: {
    htmlTitle: stringFormatter(__('search.modelPage.htmlTitle')),
    metaDescription: stringFormatter(__('search.modelPage.metaDescription')),
  },
  [SEO_PAGE_SUBTYPE]: {
    htmlTitle: stringFormatter(__('search.subTypePage.htmlTitle')),
    metaDescription: stringFormatter(__('search.subTypePage.metaDescription')),
  },
  [SEO_PAGE_CITY]: {
    h1Title: stringFormatter(__('search.page.adTitleCity')),
    htmlTitle: stringFormatter(__('search.page.htmlTitleCity')),
    metaDescription: stringFormatter(__('search.page.metaDescriptionCity')),
  },
  [SEO_PAGE_CITY_MAKE]: {
    h1Title: stringFormatter(__('search.page.adTitleCityMake')),
    htmlTitle: stringFormatter(__('search.page.htmlTitleCityMake')),
    metaDescription: stringFormatter(__('search.page.metaDescriptionCityMake')),
  },
  [SEO_PAGE_CITY_MODEL]: {
    h1Title: stringFormatter(__('search.page.adTitleCityModel')),
    htmlTitle: stringFormatter(__('search.page.htmlTitleCityModel')),
    metaDescription: stringFormatter(
      __('search.page.metaDescriptionCityModel'),
    ),
  },
  [DEFAULT_SEO_KEY]: {
    h1Title: stringFormatter(__('search.page.adTitle')),
    htmlTitle: stringFormatter(__('search.page.defaultHtmlTitle')),
    metaDescription: stringFormatter(__('search.page.defaultMetaDescription')),
  },
};

const getProps = (state, props) => props;
const getCms = flow(getProps, get(['cms']));
const getFormName = constant(SEARCH_ADS_FORM);
const withExtraFilters = flow(getProps, get(['withExtraFilters']));
const withMerchantInfo = flow(getProps, get(['withMerchantInfo']));
const withWelcomeBanner = flow(getProps, get(['withWelcomeBanner']));

const getSearchAdsForm = createSelector(
  getFormValues(SEARCH_ADS_FORM),
  (form) => form || {},
);

const getQueryFromLocationSearch = createSelector(
  (state, { location }) => location.search,
  parseAdsSearch,
);

const getSeoTextsByLinkType = createSelector(
  get(['link-type']),
  get(['ad-title']),
  get(['h1-title']),
  get(['meta-description']),
  get(['html-title']),
  (linkType, adTitle, h1Title, metaDescription, htmlTitle) => {
    let formatterArgs = [
      {
        title: adTitle,
      },
    ];

    if (
      linkType === SEO_PAGE_CITY ||
      linkType === SEO_PAGE_CITY_MODEL ||
      linkType === SEO_PAGE_CITY_MAKE
    ) {
      const [city, makeModel] = adTitle.split(',');

      formatterArgs = [
        {
          city,
          makeModel,
        },
      ];
    }

    const getText = (text, linkType, typeKey) =>
      text ||
      invokeArgs([linkType, typeKey], formatterArgs, formatters) ||
      invokeArgs([DEFAULT_SEO_KEY, typeKey], formatterArgs, formatters);

    return {
      h1Title: getText(h1Title, linkType, 'h1Title'),
      htmlTitle: getText(htmlTitle, linkType, 'htmlTitle'),
      metaDescription: getText(metaDescription, linkType, 'metaDescription'),
    };
  },
);

const getSeoTextByKey = (typeKey) =>
  flow(getSeoTextsByLinkType, get([typeKey]));

const getContent = flow(
  getCms,
  createStructuredSelector({
    title: getSeoTextByKey('h1Title'),
    content: get(['main-content']),
  }),
);

const getHeadFromCms = createSelector(
  getCms,
  createStructuredSelector({
    title: getSeoTextByKey('htmlTitle'),
    description: getSeoTextByKey('metaDescription'),
    robots: get(['meta-robots']),
  }),
);

const getHeadProps = createSelector(
  getCanonicalUrl(getCms),
  getHeadFromCms,
  (canonicalUrl, cmsHead) => ({
    canonicalUrl,
    ...cmsHead,
  }),
);

const getWelcomeBannerPropsFromCms = createSelector(
  getCms,
  createStructuredSelector({
    title: get(['h1-title']),
    subTitle: get(['banner-subtitle']),
    imageUrl: get(['banner-image-url']),
  }),
);

const isLoaded = get(['searchLayout', 'ads', 'isLoaded']);
const isSearchLoading = get(['searchLayout', 'ads', 'isLoading']);
const isModalActive = get(['searchLayout', 'ui', 'isModalFiltersActive']);

const getFavoritesTotalCount = createSelector(
  get(['searchLayout', 'ads', 'result']),
  flow(at(['likeCount', 'dislikeCount']), sum),
);

const isUnpublishedAdSearch = createSelector(
  (state, { location }) => location.search,
  (search) => {
    const parsedSearch = parseAdsSearch(search);

    return parsedSearch[PUBLISHED_NAME] === 'false';
  },
);

const withPromoBanner = flow(getProps, get(['withPromoBanner']));

const getAdListProps = createSelector(
  createAdItemsGetter(
    get(['searchLayout', 'ads', 'result', 'items']),
    getCms,
    withPromoBanner,
  ),
  get(['searchLayout', 'ads', 'result', 'pagination']),
  isSearchLoading,
  withMerchantInfo,
  (adItems, adsPagination, isSearchLoading, withMerchantInfo) => {
    const props = {
      items: adItems,
      pagination: adsPagination,
    };

    return {
      ...props,
      isSearchLoading,
      withMerchantInfo,
    };
  },
);

const getActiveFiltersByForm = createSelector(
  getSearchAdsForm,
  selectManufacturerOptions,
  (form, makeOptions) => formToFilters(makeOptions)(form),
);

const getActiveFiltersByLocation = createSelector(
  (state, props) => get(['location', 'search'], props),
  selectManufacturerOptions,
  selectCarMakeValues,
  (search, makeOptions, carMakeValues) =>
    flow(
      parseAdsSearch,
      formFromQuery(carMakeValues),
      formToFilters(makeOptions),
    )(search),
);

const getActiveFiltersListProps = createStructuredSelector({
  filters: getActiveFiltersByLocation,
});

const hasActiveFilters = createSelector(
  getActiveFiltersByLocation,
  (filters) => !isEmpty(filters),
);

const isDistanceSortDisabled = createSelector(
  getQueryFromLocationSearch,
  (query) => !query.lat && !query.lng,
);

const hasFavorites = createSelector(
  getFavoritesTotalCount,
  (totalCount) => totalCount > 0,
);

const getTopOptionsProps = createStructuredSelector({
  isDistanceSortDisabled,
  resultsAmount: get(['searchLayout', 'ads', 'result', 'exactAdsCount']),
  hasActiveFilters,
  hasFavorites,
  favoritesTotalCount: getFavoritesTotalCount,
});

const getSellWidgetHtml = createSelector(
  get(['sellWidget', 'result']),
  (data) => {
    const { body_content: html = '', footer_content: js = '' } = data || {};

    return { html, js };
  },
);

const isExtraFiltersOpened = createSelector(
  getQueryFromLocationSearch,
  flow(get([EXTRA_FILTERS_QUERY_KEY]), Boolean),
);

const getExtraFiltersContent = createStructuredSelector({
  activeFilters: getActiveFiltersByForm,
  cmsContent: getContent,
  formName: getFormName,
});

const getFavoritesCounterProps = createSelector(
  getFavoritesTotalCount,
  (totalCount) => ({
    totalCount,
  }),
);

const getEnableStickyTopBar = () => isMobile || isTablet;

const getSearchContentProps = createStructuredSelector({
  LeftSide: flow(getProps, get(['LeftSide'])),
  activeFiltersListProps: getActiveFiltersListProps,
  adListProps: getAdListProps,
  breadcrumbs: flow(getProps, get(['breadcrumbs'])),
  breadcrumbsHtml: flow(getProps, get(['breadcrumbsHtml'])),
  cmsContent: getContent,
  formName: getFormName,
  initialForm: flow(getProps, get(['initialForm'])),
  isModalActive,
  sellWidgetHtml: getSellWidgetHtml,
  topOptionsProps: getTopOptionsProps,
  welcomeBannerProps: getWelcomeBannerPropsFromCms,
  linkBoxProps: (state, props) => {
    const links = flow(getCms, get(['links']))(state, props);
    const linkType = flow(getCms, get(['link-type']))(state, props);
    const breadcrumbs = flow(getCms, get(['breadcrumbs-meta']))(state, props);

    return {
      ...links,
      breadcrumbs,
      linkType,
    };
  },
  withExtraFilters,
  withWelcomeBanner,
  isUnpublishedAdSearch,
  hasActiveFilters,
  favoritesCounterProps: getFavoritesCounterProps,
  enableStickyTopBar: getEnableStickyTopBar,
  exactAdsCount: get(['searchLayout', 'ads', 'result', 'exactAdsCount']),
});

export default createStructuredSelector({
  extraFiltersContentProps: getExtraFiltersContent,
  headProps: getHeadProps,
  isExtraFiltersOpened,
  isLoaded,
  searchContentProps: getSearchContentProps,
});
